import { Module, VuexModule, MutationAction, getModule, Action, Mutation } from 'vuex-module-decorators';
import store from '@/store';
import { i18n } from '@/i18n';
import {
    setPaymentInformation,
    estimateShipping,
    getPaymentMethods,
    getCoupon,
    getSelectedPaymentMethod,
    setPaymentMethod,
    assignAddress,
    doApplyCoupon,
    getGiftCardCredit,
    applyGiftCard,
    removeGiftCard,
    deleteCoupon,
    setShippingInformation,
    setPaymentInformationWithoutPlace,
    blackboxCheckPromoOnTicket,
    collectCartInfo,
    assignEmailToQuote,
    saveRequestInvoice,
    isSessionValid,
    validateBeforePlace,
} from '../api';
import {
    PaymentInformationRequest,
    ShippingMethod,
    PaymentMethod,
    EstimateShippingRequest,
    UpdatePaymentRequest,
    AssignAddressRequest,
    CheckoutStep,
    Coupon,
    GiftCard,
    ShippingInformationResponse,
    ShippingInfoData, QuoteAddress, RequestInvoicePayload, QuoteAddressInvoiceRequest,
} from '../models';
import cart from './cart';
import user from './user';
import storelocator from './storelocator';
import { initState, blankState } from '../stateinit';
import { copyAddressData, isAddressFulfilled, normalizeQuoteAddress, sortCustomAttributes, areEqualAddresses } from '@/commons/addressNormalizer';
import to from 'await-to-js';
import handleError from '@/commons/errorHandler';
import {decode as decodeShipment, encode as encodeShipment, getSimilarMethod, isRuPickupMethod} from '@/commons/shipmentMethodHelpers';
import fidelitycard from './fidelitycard';
import pickuppoint from './pickuppoint';
import { get as dataGetter } from 'lodash';
import cms from './cms';

function compareAddressForBillingSameAsShipping() {
    const state = window.__INITIAL_STATE__;
    if (state
        && state.billingAddress && state.billingAddress.firstname
        && state.shippingAddress && state.shippingAddress.firstname
    ) {
        return areEqualAddresses(state.billingAddress, state.shippingAddress);
    }
    return true;
}

@Module({
    namespaced: true,
    name: 'checkout',
    store,
    dynamic: true,
})
class CheckoutModule extends VuexModule {
    public billingAddress: QuoteAddress = sortCustomAttributes(initState('billingAddress'));
    public shippingAddress: QuoteAddress = sortCustomAttributes(initState('shippingAddress'));
    public emptyShippingAddress: QuoteAddress = blankState('shippingAddress');
    public email: string = initState('checkoutEmail');
    public coupon: Coupon = initState('coupon');
    public giftCard: GiftCard = initState('giftCard');
    public selectedPayment: string = initState('selectedPayment');
    public selectedShipment: string = initState('selectedShipment');
    public shipmethodSelection: string = initState('selectedShipment');
    public shippingMethods: ShippingMethod[] = initState('shippingMethods');
    public paymentMethods: PaymentMethod[] = initState('paymentMethods');
    public steps: CheckoutStep[] = initState('steps');
    public orderId: number = 0;
    public additionalData: string[] = [];
    public placeErrors: {[key: string]: string} = {};
    public editShippingAddress: boolean = false;
    public placingOrder: boolean = false;
    public shippingSameAsBillingFlag: boolean = compareAddressForBillingSameAsShipping();
    public selectedAddressId: any = {
        shipping: 0,
        billing: 0,
    };

    get currentStep(): CheckoutStep {
        const currentStep = this.steps.find(((step: CheckoutStep) => step.active ));
        return (currentStep as CheckoutStep);
    }

    get stepByName(): any {
        return (name: any) => {
            const currentStep = this.steps.find(((step: CheckoutStep) => step.name === name ));
            return (currentStep as CheckoutStep);
        };
    }

    get findShippingMethod(): (methodName: string) => ShippingMethod {
        return (methodName: string) => {
            const foundSelectedShippingMethod = this.shippingMethods.find((m) => {
                return encodeShipment(m) === methodName;
            });
            return foundSelectedShippingMethod as ShippingMethod;
        };
    }

    get hasShippingMethod(): (methodName: string) => boolean {
        return (methodName: string) => {
            const foundSelectedShippingMethod = this.shippingMethods.find((m) => {
                return encodeShipment(m) === methodName;
            });
            return foundSelectedShippingMethod ? true : false;
        };
    }

    get hasPaymentMethod(): (methodName: string) => boolean {
        return (methodName: string) => {
            const found = this.allPaymentMethods.find((m) => m.code === methodName);
            return found ? true : false;
        };
    }

    get defaultShippingAddress(): QuoteAddress {
        return this.shippingAddress;
    }

    get isBillingAddressFulfilled(): boolean {
        return isAddressFulfilled(this.billingAddress);
    }

    get isShippingAddressFulfilled(): boolean {
        return isAddressFulfilled(this.shippingAddress);
    }

    get isShippingSelectionFulfilled(): boolean {
        if (cart.cart.is_virtual) {
            return false;
        }

        if (this.selectedShipment.toString() === '') {
            return false;
        }

        if (this.isDefaultStorePickup) {
            return storelocator.selectedStoreId.toString() !== '';
        }else if (this.selectedShipment === 'pickuppoints_pickuppoints') {
            return pickuppoint.selectedPoint.id !== '';
        } else {
            if (!isAddressFulfilled(this.shippingAddress)) {
                return false;
            }

        }
        return true;
    }

    get isPaymentSelectionFulfilled(): boolean {
        return this.selectedPayment.toString() !== '';
    }

    get paymentBillingAddress(): QuoteAddress {
        const billingAddress = this.billingAddress;

        if (!billingAddress.telephone && !cart.cart.is_virtual && this.shippingAddress.telephone) {
            billingAddress.telephone = this.shippingAddress.telephone;
        }
        return normalizeQuoteAddress(billingAddress);
    }

    get selectedShippingMethod(): ShippingMethod {
        const decodedShipment = decodeShipment(this.selectedShipment);
        return this.shippingMethods.find((sm: ShippingMethod) => sm.carrier_code === decodedShipment.carrier_code
            && sm.method_code === decodedShipment.method_code) as ShippingMethod;
    }

    get selectedShippingMethodAsString(): string {
        return this.selectedShipment;
    }

    get allPaymentMethods(): PaymentMethod[] {
        const paymentMethods = this.paymentMethods;
        // if (store.state.options.payflow === 'amazon') {
        //    return paymentMethods.filter((pm: PaymentMethod) => pm.code === 'amazon_payment')
        // }
        return paymentMethods;
    }

    get canPlaceOrder(): boolean {
        if (store.state.options.payflow === 'amazon') {
            return true;
        }

        // controllo step fulfilled
        const stepsToFulfill = this.steps.filter((step) => step.name !== 'review');
        if (!stepsToFulfill.every((step) => step.unlocked && step.fulfilled)) {
            return false;
        }

        // controllo selezione pagamento
        if (!this.selectedPaymentMethod) {
            return false;
        }

        // controllo spedizione
        if (this.canShip && !this.selectedShipment) {
            return false;
        }
        if (this.isDefaultStorePickup && storelocator.selectedStoreId.toString() === '') {
            return false;
        }

        // controllo indirizzo fatturazione
        const billingAddress = this.paymentBillingAddress;
        if (!isAddressFulfilled(billingAddress)) {
            return false;
        }

        // controllo indirizzo spedizione
        if (this.canShip) {
            const shippingAddress = this.shippingAddress;
            if (!isAddressFulfilled(shippingAddress)) {
                return false;
            }
        }

        // controllo agreements
        if (!cms.areRequiredAgreementsForPlaceOrderApproved) {
            return false;
        }

        return true;
    }

    get canShip(): boolean {
        return !cart.cart.is_virtual && !this.isStorePickup;
    }

    get isRuStorePickup(): boolean {
        const isMulticarrier = store.state.options.carrier.multicarrier;
        if (isMulticarrier) {
            const selectedMethod = this.selectedShippingMethod;
            return !cart.cart.is_virtual && selectedMethod && isRuPickupMethod(selectedMethod);
        }
        return false;
    }

    get isDefaultStorePickup(): boolean {
        return this.selectedShipment === store.state.options.carrier.pickup_method;
    }

    get isStorePickup(): boolean {
        return this.isDefaultStorePickup || this.isRuStorePickup;
    }

    get selectedPaymentMethod(): PaymentMethod {
        if (store.state.options.payflow === 'amazon') {
           return {
               code: 'amazon_payment',
               title: 'Amazon',
           };
        }
        return this.paymentMethods.find((pm: PaymentMethod) => pm.code === this.selectedPayment) as PaymentMethod;
    }

    get isInvoiceRequested(): boolean {
        if (this.billingAddress.extension_attributes && this.billingAddress.extension_attributes.invoicerequest) {
            return this.billingAddress.extension_attributes.invoicerequest.enabled;
        }
        return false;
    }

    get invoiceRequestData(): QuoteAddressInvoiceRequest|undefined {
        if (this.billingAddress.extension_attributes && this.billingAddress.extension_attributes.invoicerequest) {
            return this.billingAddress.extension_attributes.invoicerequest;
        }
    }

    get hasPlaceErrorByCode(): (code: string) => boolean {
        return (code: string) => this.placeErrors.hasOwnProperty(code);
    }

    @Mutation
    public setAdditionalData(value: any) {
        this.additionalData = value;
    }

    @Mutation
    public setCouponCode(value: string) {
        this.coupon.coupon = value;
    }

    @Mutation
    public setCoupon(value: Coupon) {
        this.coupon = value;
    }

    @Mutation
    public setGiftCardCode(code: string) {
        this.giftCard.code = code;
    }

    @Mutation
    public setGiftCardVerified(verified: boolean) {
        this.giftCard.verified = verified;
    }

    @Mutation
    public setGiftCard(giftCard: GiftCard) {
        this.giftCard = giftCard;
    }

    @Mutation
    public setOrderId(value: number) {
        this.orderId = value;
    }

    @Mutation
    public setEmail(value: string) {
        this.email = value;
    }

    @Mutation
    public setBillingAddress(address: QuoteAddress) {
        this.billingAddress = normalizeQuoteAddress(address);
    }

    @Mutation
    public setShippingAddress(address: QuoteAddress) {
        this.shippingAddress = normalizeQuoteAddress(address);
    }

    @Mutation
    public copyBillingAddressToShippingAddress() {
        copyAddressData(this.billingAddress, this.shippingAddress);
    }

    @Mutation
    public setShipmethodSelection(value: string) {
        this.shipmethodSelection = value;
    }

    @Mutation
    public setSelectedShipment(value: string) {
        this.selectedShipment = value;
    }

    @Mutation
    public setSelectedPayment(value: string) {
        this.selectedPayment = value;
    }

    @Mutation
    public flagEditShippingAddress(flag: boolean) {
        this.editShippingAddress = flag;
    }

    @Mutation
    public flagPlacingOrder(flag: boolean) {
        this.placingOrder = flag;
    }

    @Mutation
    public setRequestInvoice(payload: QuoteAddressInvoiceRequest) {
        if (this.billingAddress.extension_attributes) {
            this.billingAddress.extension_attributes.invoicerequest = payload;
        }
    }

    @Mutation
    public setShippingMethods(methods: ShippingMethod[]) {
        this.shippingMethods = methods;
    }

    @Mutation
    public setSelectedAddressId(payload: any) {
        this.selectedAddressId[payload.type] = payload.id;
    }

    @Mutation
    public setShippingSameAsBillingFlag(value: boolean) {
        this.shippingSameAsBillingFlag = value;
    }

    @Mutation
    public addPlaceError(payload: any) {
        this.placeErrors[payload.code] = payload.message;
    }

    @Mutation
    public resetPlaceErrors(code?: any) {
        if (code) {
            if (this.placeErrors.hasOwnProperty(code)) {
                delete this.placeErrors[code];
            }
        } else {
            this.placeErrors = {};   
        }
    }

    @Mutation
    public reset() {
        this.billingAddress = blankState('billingAddress');
        this.shippingAddress = blankState('shippingAddress');
        this.email = blankState('checkoutEmail');
        this.coupon = blankState('coupon');
        this.selectedPayment = blankState('selectedPayment');
        this.selectedShipment = blankState('selectedShipment');
        this.shippingMethods = blankState('shippingMethods');
        this.paymentMethods = blankState('paymentMethods');
        this.steps = blankState('steps');
        this.additionalData = [];
        this.editShippingAddress = false;
        this.placingOrder = false;
    }

    @Mutation
    public resetSteps(currentStep: string) {
        let keepUnlocking = true;
        this.steps.forEach((step: CheckoutStep) => {
            const stepIndex = this.steps.findIndex(((s: CheckoutStep) => s.name === step.name ));
            this.steps[stepIndex].active = false;
            this.steps[stepIndex].unlocked = false;
            if (keepUnlocking) {
                this.steps[stepIndex].unlocked = true;
            }
            if (step.name === currentStep) {
                this.steps[stepIndex].active = true;
                keepUnlocking = false;
            }
        });
    }

    @Mutation
    public goingToStep(payload: {fromStep: string, toStep: string}) {
        const fromStepIndex = this.steps.findIndex(((step: CheckoutStep) => step.name === payload.fromStep ));
        this.steps[fromStepIndex].fulfilled = true;

        for (const s in this.steps) {
            if (this.steps.hasOwnProperty(s)) {
                this.steps[s].active = false;
            }
        }
        const stepIndex = this.steps.findIndex(((step: CheckoutStep) => step.name === payload.toStep ));
        this.steps[stepIndex].unlocked = true;
        this.steps[stepIndex].active = true;
    }

    @Mutation
    public resetStep(stepName: string) {
        const stp = this.steps.find(((step: CheckoutStep) => step.name === stepName ));
        if (stp) {
            stp.fulfilled = false;
            stp.unlocked = false;
        }
    }

    @Mutation
    public setPaymentMethods(paymentMethods: PaymentMethod[]) {
        if (paymentMethods && store.state.options.payment && store.state.options.payment.costs) {
            const paymentCosts = store.state.options.payment.costs;
            paymentMethods.forEach((method: PaymentMethod) => {
                if (paymentCosts.hasOwnProperty(method.code)) {
                    method.cost = paymentCosts[method.code];
                }
            });
        }

        if (paymentMethods && store.state.options.payment.withelisted) {
            paymentMethods = paymentMethods.filter((method) => {
                return store.state.options.payment.withelisted.includes(method.code);
            });
        }

        // se ho selezionato il metodo di pagamento ma non esiste più tra la lista dei metodi resetto la selezione
        if (paymentMethods
            && this.selectedPayment
            && !paymentMethods.find((m) => m.code === this.selectedPayment)
        ) {
            this.selectedPayment = '';
        }

        this.paymentMethods = paymentMethods;
    }

    @Action
    public initializeStepStatus() {
        if (this.email.toString() !== '') {
            this.goingToStep({fromStep: 'userdata', toStep: 'invoice'});
        }

        if (this.isBillingAddressFulfilled) {
            this.goingToStep({ fromStep: 'invoice', toStep: 'shipping' });
            if (cart.cart.is_virtual) {
                this.goingToStep({ fromStep: 'shipping', toStep: 'payment' });
            }
        }

        if (this.isShippingSelectionFulfilled) {
            this.goingToStep({ fromStep: 'shipping', toStep: 'payment' });
        }

        if (this.isPaymentSelectionFulfilled) {
            this.goingToStep({ fromStep: 'payment', toStep: 'review'});
        }
    }

    @Action({rawError: true})
    public validateBeforePlace() {
        this.resetPlaceErrors();
        let grandTotal = cart.totals.grand_total;
        if (cart.totals.base_currency_code !== cart.totals.quote_currency_code) {
            grandTotal = cart.totals.base_grand_total;
        }

        const unlockedSteps = this.steps.filter((step) => step.name !== 'review' && step.unlocked).map((step) => step.name);
        const isShippingUnlocked = unlockedSteps.indexOf('shipping') !== -1;
        const isPamentUnlocked = unlockedSteps.indexOf('payment') !== -1;

        const fulfilledSteps = this.steps.filter((step) => step.name !== 'review' && step.fulfilled).map((step) => step.name);
        const isInvoiceFulfilled = fulfilledSteps.indexOf('invoice') !== -1;
        const isShippingFulfilled = fulfilledSteps.indexOf('shipping') !== -1;

        const isStorePickupMethodSelected = this.shipmethodSelection === store.state.options.carrier.pickup_method;
        const isPickuppointSelected = this.shipmethodSelection === 'pickuppoints_pickuppoints';

        // controllo selezione pagamento
        if (isPamentUnlocked && !this.selectedPaymentMethod) {
            this.addPlaceError({code: 'payment_selection', message: i18n.t('To proceed you must select a payment method').toString()});
        }

        // controllo spedizione
        if (isShippingUnlocked && this.canShip && !this.shipmethodSelection) {
            this.addPlaceError({code: 'shipping_selection', message: i18n.t('To proceed you need to save the selected shipping address').toString()});
        }
        if (isShippingUnlocked && isStorePickupMethodSelected && storelocator.selectedStoreId.toString() === '') {
            this.addPlaceError({code: 'pickup_selection', message: i18n.t('To proceed you need to select the store').toString()});
        }
        if (isShippingUnlocked && isPickuppointSelected && pickuppoint.selectedPoint.id === '') {
            this.addPlaceError({code: 'pickuppoint_selection', message: i18n.t('To proceed you need to select the pickup point').toString()});
        }
        if (!isShippingFulfilled) {
            this.addPlaceError({code: 'shipping_address', message: i18n.t('To proceed you need to save the selected shipping address').toString()});
        }

        // controllo klarna
        const isKlarna = this.selectedPayment === 'klarna_pay_later';
        const klarnaThreshold = store.state.options.payment.klarnaConfig.threshold;
        if (isKlarna && klarnaThreshold && grandTotal < klarnaThreshold) {
            this.addPlaceError({code: 'klarna_threshold', message: i18n.t('klarna-threshold-warning', {amount: klarnaThreshold}).toString()});
        }

        // controllo indirizzo fatturazione
        if (!isInvoiceFulfilled) {
            this.addPlaceError({code: 'invoice_address', message: i18n.t('To proceed you need to save the selected billing address').toString()});
        }

        // controllo agreements
        const agreementsOk = cms.areRequiredAgreementsForPlaceOrderApproved;
        if (!agreementsOk) {
            this.addPlaceError({code: 'agreements', message: i18n.t('To proceed it is necessary to accept the general conditions of sale').toString()});
        }
    }

    @Action({rawError: true})
    public async validateBeforePay(): Promise<boolean> {
        const billingAddress = this.paymentBillingAddress;

        let sessionErr: any;
        let addressErr: any;
        let resp: any;
        let checkpromoErr: any;
        let checkpromoResp: any;
        let isPromoOnTicketValid = true;
        [ sessionErr, resp ] = await to(isSessionValid());
        const isSessionValidCheck: boolean = !sessionErr && resp;
        [ addressErr, resp ] = await to(validateBeforePlace(billingAddress, this.email));
        const isAddressValidCheck = !addressErr && resp;

        if (cart.isBlackboxEnabled) {
            const blockingBlackboxErrors = store.state.options.blackbox.blocking_errorcodes;
            [ checkpromoErr, checkpromoResp ] = await to(this.checkPromoOnTicket());
            isPromoOnTicketValid = !checkpromoErr
                || (checkpromoErr && !blockingBlackboxErrors.includes(dataGetter(checkpromoErr, 'response.data.code')));
        }

        let hasErrors = false;

        if (!isSessionValidCheck) {
            handleError(i18n.t('Session expired please reload the page').toString(), false);
            hasErrors = true;
        }

        if (!isAddressValidCheck) {
            handleError(addressErr, false);
            hasErrors = true;
        }

        if (!isPromoOnTicketValid) {
            store.commit('removeWarningByType', 'blackbox');
            handleError(checkpromoErr, false);
            hasErrors = true;
        }

        return new Promise((resolve, reject) => {
            resolve(!hasErrors);
        });
    }

    @Action({rawError: true})
    public async placeOrder(request: PaymentInformationRequest): Promise<string> {
        let orderId: any;
        let err: any;
        [ err, orderId ] = await to(setPaymentInformation(request));
        if (err) {
            orderId = 0;
        }
        this.setOrderId(orderId);
        return new Promise((resolve, reject) => {
            if (err) {
                reject(err);
            } else {
                resolve(orderId);
            }
        });
    }

    @Action({rawError: true})
    public async putPaymentInformation(request: PaymentInformationRequest): Promise<string> {
        let setRes: any;
        let err: any;
        [ err, setRes ] = await to(setPaymentInformationWithoutPlace(request));

        return new Promise((resolve, reject) => {
            if (err) {
                reject(err);
            } else {
                resolve('pending_order');
            }
        });
    }

    @Action
    public async estimateShipping(address: QuoteAddress|null) {
        let shippingMethods: ShippingMethod[] | undefined;
        let err;
        const addressReq: QuoteAddress = address || initState('address');
        const req: EstimateShippingRequest = { address: normalizeQuoteAddress(addressReq) };
        [ err, shippingMethods ] = await to(estimateShipping(req));
        if (err) {
            shippingMethods = [];
            handleError(err);
        }
        this.shippingMethodsAssign(shippingMethods as ShippingMethod[]);
    }

    @Action
    public async estimateShippingSimple(address: QuoteAddress|null) {
        let shippingMethods: ShippingMethod[] | undefined;
        let err;
        const addressReq: QuoteAddress = address || initState('address');
        const req: EstimateShippingRequest = { address: normalizeQuoteAddress(addressReq) };
        [ err, shippingMethods ] = await to(estimateShipping(req));
        if (err) {
            shippingMethods = [];
            handleError(err);
        }
        this.setShippingMethods(shippingMethods as ShippingMethod[]);
    }

    @Action
    public async shippingMethodsAssign(shippingMethods: ShippingMethod[]) {
        // se ho selezionato il metodo di spedizione ma non esiste più tra la lista dei metodi resetto la selezione
        if (shippingMethods && this.selectedShippingMethod) {
            const foundSelectedShippingMethod = shippingMethods.find((m) => {
                return encodeShipment(m) === encodeShipment(this.selectedShippingMethod);
            });
            const shouldSelectSimilarMethod = store.state.options.carrier.similarmethod;

            if (!foundSelectedShippingMethod) {
                const similarMethod = getSimilarMethod(this.selectedShippingMethod, shippingMethods);
                if (similarMethod && shouldSelectSimilarMethod && isAddressFulfilled(this.defaultShippingAddress)) {
                    this.setSelectedShipment(encodeShipment(similarMethod));
                    this.setShippingInfo({
                        address: this.defaultShippingAddress,
                        shippingmethod: decodeShipment(this.selectedShipment),
                    });
                } else {
                    this.setSelectedShipment('');
                }
            }
        }
        this.setShippingMethods(shippingMethods);
    }

    @MutationAction({ mutate: ['paymentMethods'] })
    public async getPaymentMethods() {
        let paymentMethods: PaymentMethod[] | undefined;
        let err;
        [ err, paymentMethods] = await to(getPaymentMethods());
        if (err) {
            paymentMethods = [];
            handleError(err);
        }
        if (paymentMethods && store.state.options.payment && store.state.options.payment.costs) {
            const paymentCosts = store.state.options.payment.costs;
            paymentMethods.forEach((method: PaymentMethod) => {
                if (paymentCosts.hasOwnProperty(method.code)) {
                    method.cost = paymentCosts[method.code];
                }
            });
        }

        if (paymentMethods && store.state.options.payment.withelisted) {
            paymentMethods = paymentMethods.filter((method) => {
                return store.state.options.payment.withelisted.includes(method.code);
            });
        }

        // se ho selezionato il metodo di pagamento ma non esiste più tra la lista dei metodi resetto la selezione
        if (paymentMethods && this.getters && this.getters.selectedPaymentMethod) {
            const selectedPaymentMethod = this.getters.selectedPaymentMethod as unknown as PaymentMethod;
            if (selectedPaymentMethod && !paymentMethods.find((m) => m.code === selectedPaymentMethod.code)) {
                const self = this as any;
                self.commit('setSelectedPayment', '');
            }
        }

        return { paymentMethods };
    }

    @MutationAction({ mutate: ['coupon'] })
    public async getCoupon() {
        let couponCode;
        let applied;
        let err;
        [ err, couponCode ] = await to(getCoupon());
        if (err) {
            applied = false;
            couponCode = '';
            handleError(err, false);
        } else {
            applied = couponCode.length > 0;
        }
        return { coupon: { coupon: couponCode, applied } };
    }

    @Action
    public async applyCoupon(couponCode: string) {
        let applied;
        let couponerr = '';
        let err;
        let applyRes: any;
        [ err, applyRes ] = await to(doApplyCoupon(couponCode));
        if (err) {
            applied = false;
            couponCode = '';
            couponerr = handleError(err, false, false).message;
        } else {
            applied = applyRes;
        }
        this.setCoupon({ coupon: couponCode, applied: applied as boolean, error: couponerr });

        if (this.giftCard.applied) {
            this.removeGiftCard();
        }

        await this.collectCartInfo(['totals', 'shipping', 'payment', 'fidelity']);
        return new Promise((resolve, reject) => {
            resolve(applyRes);
        });
    }

    @Action
    public async removeCoupon() {
        let applied;
        let err;
        let applyRes;
        let cRes;
        let cErr;
        [ err, applyRes ] = await to(deleteCoupon());
        if (err) {
            applied = false;
            handleError(err);
        } else {
            applied = !applyRes;
        }
        this.setCoupon({ coupon: '', applied: applied as boolean, error: '' });

        if (this.giftCard.applied) {
            this.removeGiftCard();
        }

        cart.getTotals();
        this.estimateShipping(this.shippingAddress);
        this.getPaymentMethods();
        [ cErr, cRes ] = await to(this.checkPromoOnTicket());
        if (cErr) {
            handleError(cErr, false);
        }
    }

    @Action({rawError: true})
    public async checkPromoOnTicket() {
        let err: any;
        let res: any;

        [ err, res ] = await to(blackboxCheckPromoOnTicket());

        this.context.commit('removeWarningByType', 'blackbox', { root: true });

        if (res && res.totals) {
            cart.updateTotals(res.totals);
        }

        if (res && res.extgtm_data) {
            var gtmData = JSON.parse(res.extgtm_data);
            if(gtmData.product_data){
                var productdata = gtmData.product_data;
                for (var key in productdata) {
                    var foundIndex = cart.cart.items.findIndex((citem: any) => citem.item_id == key);
                    if (foundIndex >= 0) {
                        cart.cart.items[foundIndex].extension_attributes.extgtm_data = JSON.stringify(productdata[key]);
                    }
                }
            }
            if(gtmData.extra_events){
                store.state.options.gtm.extraEventsAjax = gtmData.extra_events;
            }
        }


        if (res && res.shipping_methods && res.shipping_methods.length > 0) {
            this.shippingMethodsAssign(res.shipping_methods as ShippingMethod[]);
        }
        if (res && res.payment_methods) {
            this.setPaymentMethods(res.payment_methods);
        }

        if (res && res.fidelity_data) {
            if (!res.fidelity_data.applied_bonus) {
                res.fidelity_data.applied_bonus = 0;
            }
            fidelitycard.setFidelityData(res.fidelity_data);
        }

        return new Promise((resolve, reject) => {
            if (err) {
                cart.getTotals();
                reject(err);
                const parsedErr = handleError(err, false, false);
                store.state.options.warnings.push({type: 'blackbox', message: parsedErr.message});
            } else {
                resolve(res);
            }
        });
    }

    @Action({rawError: true})
    public async collectCartInfo(infosToCollect: string[]) {
        let err;
        let res;
        cart.flagLoading({key: 'totals', value: true});
        // cart.flagLoading({key: 'shipMethods', value: true});
        [ err, res ] = await to(collectCartInfo(infosToCollect));
        cart.flagLoading({key: 'totals', value: false});
        // cart.flagLoading({key: 'shipMethods', value: false});

        if (res && res.totals) {
            cart.updateTotals(res.totals);
        }
        if (res && res.shipping_methods && res.shipping_methods.length > 0) {
            this.shippingMethodsAssign(res.shipping_methods as ShippingMethod[]);
        }
        if (res && res.payment_methods) {
            this.setPaymentMethods(res.payment_methods);
        }

        if (res && res.fidelity_data) {
            if (!res.fidelity_data.applied_bonus) {
                res.fidelity_data.applied_bonus = 0;
            }
            fidelitycard.setFidelityData(res.fidelity_data);
        }
    }

    @Action
    public async getGiftCardCredit(giftCardCode: string): Promise<{err: string, credit: any}> {
        const [err, credit] = await to(getGiftCardCredit(giftCardCode));
        const giftCard = this.giftCard;

        if (!err && (credit !== 0)) {
            giftCard.code = giftCardCode;
            giftCard.credit = credit;
            this.setGiftCard(giftCard);

            return {err: '', credit};
        } else {
            return {
                err: i18n.t('The gift card is invalid or empty. Please check the gift card and try again').toString(),
                credit: 0,
            };
        }
    }

    @Action({rawError: true})
    public async applyGiftCard(amount: any) {
        let applied: boolean = false;
        let giftcarderr = '';
        const giftCard = this.giftCard;

        const [ err, applyRes ] = await to(applyGiftCard(giftCard.code, amount));

        if (!applyRes) {
            if (err) {
                giftcarderr = err.message  || 'There has been an error. Please try again';
            }
            giftcarderr = 'There has been an error. Please try again';
        } else {
            applied = true;
        }

        giftCard.applied = applied;
        giftCard.applied_credit = applyRes;
        giftCard.error = giftcarderr;

        this.setGiftCard(giftCard);
        cart.getTotals();
        this.estimateShipping(this.shippingAddress);
        this.getPaymentMethods();

        return applied;
    }

    @Action({rawError: true})
    public async removeGiftCard() {
        let applied: boolean = false;
        let giftcarderr = '';
        const giftCard = this.giftCard;

        giftCard.code = '';

        const [ err, applyRes ] = await to(removeGiftCard(giftCard.code));

        if (!applyRes) {
            if (err) {
                giftcarderr = err.message  || 'There has been an error. Please try again';
            }
            giftcarderr = 'There has been an error. Please try again';

            applied = giftCard.applied;
        }

        giftCard.applied = false;
        giftCard.verified = false;
        giftCard.error = giftcarderr;

        const ngiftCard = blankState('giftCard');
        this.setGiftCard(ngiftCard);
        cart.getTotals();
        this.estimateShipping(this.shippingAddress);
        this.getPaymentMethods();

        return applied;
    }

    @MutationAction({ mutate: ['selectedPayment'] })
    public async getSelectedPayment() {
        let selectedPayment;
        let err;
        [ err, selectedPayment ] = await to(getSelectedPaymentMethod());
        if (err) {
            selectedPayment = '';
            handleError(err);
        }
        return { selectedPayment };
    }

    @Action
    public async setPayment(paymentCode: string) {
        let selectedPayment;
        let err: any;
        let res: any;
        const request: UpdatePaymentRequest = {
            method: { method: paymentCode },
        };
        this.setSelectedPayment(paymentCode);
        cart.flagLoading({key: 'totals', value: true});
        [ err, selectedPayment ] = await to(setPaymentMethod(request));
        if (err) {
            selectedPayment = '';
            this.setSelectedPayment(selectedPayment);
            if (err.response && err.response.status === 400 && fidelitycard.hasUsed) {
                err.response.data.message = 'It is not possible to use the WE Card points for the entire amount, the minimum payment must be at least 0.05 Euros';
            }

            handleError(err);
        }
        [ err, res ] = await to(this.checkPromoOnTicket());
        if (err) {
            handleError(err, false);
        }
        cart.getTotals();
        return new Promise((resolve, reject) => {
            if (err) {
                resolve(false);
            } else {
                resolve(true);
            }
        });
    }

    @Action
    public async assignShippingAddress(address: QuoteAddress) {
        let err: any;
        const request: AssignAddressRequest = {
            address,
            useForShipping: true,
            cartId: cart.cartToken.toString() !== '' ? cart.cartToken : cart.cart.id,
        };
        [ err ] = await to(assignAddress(request));

        return new Promise((resolve, reject) => {
            if (err) {
                handleError(err);
            }
            resolve({});
            this.setShippingAddress(address);
        });
    }

    @Action
    public async assignBillingAddress(address: QuoteAddress) {
        let err: any;
        const request: AssignAddressRequest = {
            address,
            cartId: cart.cartToken.toString() !== '' ? cart.cartToken : cart.cart.id,
        };
        [ err ] = await to(assignAddress(request));

        return new Promise((resolve, reject) => {
            if (err) {
                handleError(err);
            }
            resolve({});
            this.setBillingAddress(address);
        });
    }


    @Action({rawError: true})
    public async assignEmail(email: string) {
        let err: any;
        [ err ] = await to(assignEmailToQuote(email));

        return new Promise((resolve, reject) => {
            if (err) {
                reject(err);
            } else {
                resolve(true);
                user.setEmail(email);
                this.setEmail(email);
            }
        });
    }

    @Action({rawError: true})
    public async assignRequestInvoice(payload: RequestInvoicePayload) {
        if(payload.requestInvoice.tdy_request_invoice_dob) {
            const dataOfBirth =  new Date(payload.requestInvoice.tdy_request_invoice_dob);
            const isoDate = dataOfBirth.getFullYear() + '-' +
                String(dataOfBirth.getMonth() + 1).padStart(2, '0') + '-' +
                String(dataOfBirth.getDate()).padStart(2, '0');
            payload.requestInvoice.tdy_request_invoice_dob = isoDate;
        }
        let err: any;
        [ err ] = await to(saveRequestInvoice(payload));


    }

    @Action({rawError: true})
    public async setShippingInfo(shipInfo: ShippingInfoData): Promise<ShippingInformationResponse> {
        const data = {
            addressInformation: {
              shipping_address: normalizeQuoteAddress(shipInfo.address),
              billing_address: normalizeQuoteAddress(this.billingAddress),
              shipping_method_code: shipInfo.shippingmethod.method_code,
              shipping_carrier_code: shipInfo.shippingmethod.carrier_code,
              extension_attributes: {},
            },
        };

        let err: any;
        let resp: any;
        cart.flagLoading({key: 'totals', value: true});
        [ err, resp ] = await to(setShippingInformation(data));
        cart.flagLoading({key: 'totals', value: false});
        return new Promise((resolve, reject) => {
            // cart.getTotals();
            // this.estimateShipping(data.addressInformation.shipping_address);
            if (err) {
                reject(err);
                handleError(err);
            } else {
                cart.updateTotals(resp.totals);
                this.setPaymentMethods(resp.payment_methods);
                resolve(resp);
            }
        });
    }
}

export default getModule(CheckoutModule);
